import { useCallback } from "react";
import { Bell, AlertCircle, CheckCircle2, Info, AlertTriangle, ExternalLink, Wifi, WifiOff } from "lucide-react";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Skeleton } from "../../ui/skeleton";
import { ScrollArea } from "../../ui/scroll-area";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { useNotifications, type Notification } from "@/hooks/useNotifications";

function formatTimestamp(timestamp: string): string {
  if (!timestamp) {
    return "Just now";
  }

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return "Just now";
  }

  // Notifications are past events; clamp future skew so it never reads "in N minutes".
  const now = new Date();
  const clamped = date > now ? now : date;

  try {
    return formatDistanceToNow(clamped, { addSuffix: true });
  } catch {
    return "Just now";
  }
}

function getNotificationIcon(severity: string, category: string) {
  // Urgent/warning severity takes precedence
  if (severity === "urgent") {
    return <AlertCircle className="h-4 w-4 text-red-500 dark:text-red-400" />;
  }
  if (severity === "warning") {
    return <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />;
  }

  // Category-based icons for info severity
  switch (category) {
    case "enrollment_activity":
    case "enrollment_risk":
      return <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />;
    case "terminations":
    case "cobra":
      return <AlertCircle className="h-4 w-4 text-orange-500 dark:text-orange-400" />;
    case "qle":
    case "coverage_changes":
      return <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />;
    case "eligibility_milestones":
    case "renewals":
      return <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />;
    default:
      return <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />;
  }
}

function getIconBackgroundClass(severity: string, category: string): string {
  if (severity === "urgent") {
    return "bg-red-100 dark:bg-red-500/20";
  }
  if (severity === "warning") {
    return "bg-yellow-100 dark:bg-yellow-500/20";
  }

  // Category-based backgrounds for info severity
  switch (category) {
    case "enrollment_activity":
    case "enrollment_risk":
      return "bg-green-100 dark:bg-green-500/20";
    case "terminations":
    case "cobra":
      return "bg-orange-100 dark:bg-orange-500/20";
    case "eligibility_milestones":
    case "renewals":
      return "bg-yellow-100 dark:bg-yellow-500/20";
    default:
      return "bg-blue-100 dark:bg-blue-500/20";
  }
}

function formatCategoryLabel(category: string): string {
  return category
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export default function NotificationView() {
  const {
    notifications,
    unreadCount,
    isLoading,
    wsConnected,
    markAsRead,
    markAllAsRead,
    isMarkingAllAsRead,
  } = useNotifications();

  const handleNotificationClick = useCallback((notification: Notification) => {
    // Mark as read if unread
    if (notification.status === "unread") {
      markAsRead(notification.id);
    }

    // Navigate to CTA URL if provided
    if (notification.ctaUrl) {
      // Legacy guard: some stored notifications were created with a missing
      // id interpolated into the URL (e.g. /clients/30/employees/undefined).
      // Fall back to the client's employees tab instead of a dead page.
      const brokenEmployeeUrl = notification.ctaUrl.match(
        /^\/clients\/(\d+)\/employees\/(undefined|null|NaN)$/
      );
      window.location.href = brokenEmployeeUrl
        ? `/clients/${brokenEmployeeUrl[1]}?tab=employees`
        : notification.ctaUrl;
    }
  }, [markAsRead]);

  const handleMarkAllRead = useCallback(() => {
    markAllAsRead();
  }, [markAllAsRead]);

  return (
    <div className="flex flex-col h-full -m-6">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-border bg-background">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-foreground">Notifications</h3>
          {unreadCount > 0 && (
            <Badge variant="secondary" className="text-xs">
              {unreadCount}
            </Badge>
          )}
          {/* WebSocket status indicator */}
          <span title={wsConnected ? "Real-time updates active" : "Using polling for updates"}>
            {wsConnected ? (
              <Wifi className="h-3 w-3 text-green-500 dark:text-green-400" />
            ) : (
              <WifiOff className="h-3 w-3 text-muted-foreground" />
            )}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleMarkAllRead}
          disabled={unreadCount === 0 || isMarkingAllAsRead}
        >
          {isMarkingAllAsRead ? "Marking..." : "Mark All Read"}
        </Button>
      </div>

      {/* Notification List */}
      <ScrollArea className="flex-1 bg-background">
        {isLoading && (
          <div className="p-4 space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="flex items-start gap-3 p-3">
                <Skeleton className="h-8 w-8 rounded-full flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-3 w-2/3" />
                  <Skeleton className="h-3 w-1/4" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!isLoading && notifications.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Bell className="h-12 w-12 text-muted-foreground/50 mb-3" />
            <p className="text-sm text-muted-foreground">No notifications yet</p>
            <p className="text-xs text-muted-foreground mt-1">
              Updates will appear here
            </p>
          </div>
        )}

        {!isLoading && notifications.length > 0 && (
          <div className="divide-y divide-border">
            {notifications.map(notification => (
              <div
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={cn(
                  "group flex items-start gap-3 p-4 cursor-pointer transition-colors",
                  "hover:bg-accent/50 dark:hover:bg-accent/30",
                  notification.status === "unread"
                    ? "bg-primary/5 dark:bg-primary/10"
                    : "bg-background"
                )}
              >
                {/* Icon */}
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
                  getIconBackgroundClass(notification.severity, notification.category)
                )}>
                  {getNotificationIcon(notification.severity, notification.category)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <span className={cn(
                      "flex items-center gap-1.5 min-w-0 text-sm text-foreground group-hover:text-accent-foreground",
                      notification.status === "unread" ? "font-semibold" : "font-medium"
                    )}>
                      {notification.status === "unread" && (
                        <span
                          className="h-2 w-2 rounded-full bg-primary flex-shrink-0"
                          aria-label="Unread"
                        />
                      )}
                      <span className="truncate">{notification.title}</span>
                    </span>
                    <span className="text-xs text-muted-foreground group-hover:text-accent-foreground/80 flex-shrink-0">
                      {formatTimestamp(notification.createdAt)}
                    </span>
                  </div>

                  <p className="text-xs text-muted-foreground group-hover:text-accent-foreground/90">
                    {notification.message}
                  </p>

                  <div className="flex items-center gap-2 mt-1.5">
                    <Badge
                      variant="outline"
                      className="text-xs border-border text-muted-foreground group-hover:text-accent-foreground group-hover:border-accent-foreground/40"
                    >
                      {formatCategoryLabel(notification.category)}
                    </Badge>
                    {notification.ctaLabel && (
                      <span className="text-xs text-primary dark:text-primary group-hover:text-accent-foreground flex items-center gap-1">
                        {notification.ctaLabel}
                        <ExternalLink className="h-3 w-3" />
                      </span>
                    )}
                    {notification.status === "unread" && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          markAsRead(notification.id);
                        }}
                        className="ml-auto inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium text-muted-foreground group-hover:text-accent-foreground hover:bg-accent-foreground/20 transition-colors"
                        title="Mark this notification as read"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        Mark read
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Footer */}
      {!isLoading && notifications.length > 0 && (
        <div className="px-6 py-3 border-t border-border bg-muted/30 dark:bg-muted/10">
          <p className="text-xs text-center text-muted-foreground">
            Showing {notifications.length} notifications
          </p>
        </div>
      )}
    </div>
  );
}
